/**
 * Lista de licitações com painel por etapa (D86): contadores por status, "Novas oportunidades" (favoritas da busca
 * ainda não captadas, sem cadastro no banco) e mudança de status direto na linha.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AutocompleteInput,
  CreateButton,
  Datagrid,
  DateInput,
  FunctionField,
  List,
  ReferenceInput,
  SelectInput,
  SimpleList,
  TextField,
  TextInput,
  Title,
  TopToolbar,
  useNotify,
  useRefresh,
  useStore,
} from "react-admin";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextFieldMui from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import QuadroIcon from "@mui/icons-material/ViewKanbanOutlined";
import ListaIcon from "@mui/icons-material/ViewListOutlined";
import { lerControlePncp } from "@sobral/dominio";
import { useCelular } from "../componentes";
import { formatarDataHora, formatarMoeda, normalizarBusca } from "../lib/formato";
import { linkPncp } from "../lib/pncp";
import { supabase } from "../lib/supabase";
import { ChipPrazo, DialogoItens, nomeEsfera, type Resultado } from "./busca-pncp";
import { cadastrarDoPncp } from "./licitacoes";
import { textoDaPreparacao } from "./licitacao-leitura";
import { SeletorStatus, nomeStatus } from "./licitacao-status";
import { NOME_RESULTADO } from "./licitacao-resultado";
import { QuadroLicitacoes } from "./licitacao-quadro";

/* ------------------------------------ Grupos do painel ------------------------------------ */

const EM_ANDAMENTO = ["captada", "em_analise", "em_cotacao", "proposta_pronta", "proposta_enviada", "em_disputa", "resultado", "recurso", "suspensa"];
/** Etapas sempre visíveis no painel; as demais aparecem quando têm licitações. */
const ETAPAS_FIXAS = ["captada", "em_analise", "em_cotacao", "proposta_pronta", "proposta_enviada", "em_disputa"];
const ETAPAS_EVENTUAIS = ["resultado", "recurso", "suspensa", "homologada", "descartada"];

type Contagem = Record<string, number>;


function usePainel() {
  return useQuery({
    queryKey: ["painel_licitacoes"],
    queryFn: async (): Promise<Contagem> => {
      const { data, error } = await supabase.rpc("painel_licitacoes");
      if (error) throw new Error(error.message);
      return Object.fromEntries(((data ?? []) as { grupo: string; quantidade: number }[]).map((g) => [g.grupo, g.quantidade]));
    },
  });
}

function filtroDoGrupo(grupo: string): Record<string, unknown> {
  if (grupo === "andamento") return { "status@in": `(${EM_ANDAMENTO.join(",")})` };
  if (grupo === "todas") return {};
  return { status: grupo };
}

function CartaoGrupo({ nome, quantidade, ativo, destaque, aoClicar }: { nome: string; quantidade: number | undefined; ativo: boolean; destaque?: boolean; aoClicar: () => void }) {
  return (
    <ButtonBase
      onClick={aoClicar}
      aria-pressed={ativo}
      sx={{
        flex: "0 0 auto",
        minWidth: 112,
        px: 1.5,
        py: 1,
        border: 1,
        borderRadius: 1,
        borderColor: ativo ? "secondary.main" : "divider",
        boxShadow: ativo ? (t) => `inset 0 0 0 1px ${t.palette.secondary.main}` : "none",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        textAlign: "left",
        opacity: quantidade ? 1 : 0.6,
      }}
    >
      <Typography variant="h6" component="span" sx={{ fontWeight: 700, lineHeight: 1.2, color: destaque && quantidade ? "secondary.main" : "primary.main" }}>
        {quantidade ?? "–"}
      </Typography>
      <Typography variant="caption" component="span" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
        {nome}
      </Typography>
    </ButtonBase>
  );
}

function PainelGrupos({ grupo, aoEscolher }: { grupo: string; aoEscolher: (g: string) => void }) {
  const { data: contagem = {} } = usePainel();
  const andamento = EM_ANDAMENTO.reduce((s, st) => s + (contagem[st] ?? 0), 0);
  const todas = Object.entries(contagem).reduce((s, [g, q]) => (g === "oportunidade" ? s : s + q), 0);
  const etapas = [...ETAPAS_FIXAS, ...ETAPAS_EVENTUAIS.filter((s) => (contagem[s] ?? 0) > 0 || grupo === s)];
  return (
    <Stack direction="row" spacing={1} sx={{ overflowX: "auto", pb: 1, mb: 1 }}>
      <CartaoGrupo nome="Novas oportunidades" quantidade={contagem.oportunidade ?? 0} ativo={grupo === "oportunidade"} destaque aoClicar={() => aoEscolher("oportunidade")} />
      <CartaoGrupo nome="Em andamento" quantidade={andamento} ativo={grupo === "andamento"} aoClicar={() => aoEscolher("andamento")} />
      {etapas.map((s) => (
        <CartaoGrupo key={s} nome={nomeStatus(s)} quantidade={contagem[s] ?? 0} ativo={grupo === s} aoClicar={() => aoEscolher(s)} />
      ))}
      <CartaoGrupo nome="Todas" quantidade={todas} ativo={grupo === "todas"} aoClicar={() => aoEscolher("todas")} />
    </Stack>
  );
}

/* ------------------------------------ Novas oportunidades ------------------------------------ */

function DialogoDescartarOportunidade({ oportunidade, aoFechar }: { oportunidade: Resultado; aoFechar: () => void }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [motivo, setMotivo] = useState("");
  const [gravando, setGravando] = useState(false);

  async function confirmar() {
    setGravando(true);
    const { error } = await supabase.rpc("descartar_oportunidade", { numero_controle_pncp: oportunidade.numero_controle_pncp, motivo: motivo.trim() });
    setGravando(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    notify("Oportunidade descartada. Ela fica como ignorada na busca.", { type: "success" });
    refresh();
    aoFechar();
  }

  return (
    <Dialog open onClose={gravando ? undefined : aoFechar} fullWidth maxWidth="sm">
      <DialogTitle>Descartar oportunidade</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {oportunidade.orgao_nome} — {oportunidade.numero}
        </Typography>
        <TextFieldMui autoFocus label="Motivo do descarte" value={motivo} onChange={(e) => setMotivo(e.target.value)} fullWidth multiline minRows={2} required />
      </DialogContent>
      <DialogActions>
        <Button onClick={aoFechar} disabled={gravando}>
          Cancelar
        </Button>
        <Button variant="contained" color="error" onClick={() => void confirmar()} disabled={gravando || !motivo.trim()}>
          {gravando ? "Gravando…" : "Descartar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function NovasOportunidades() {
  const notify = useNotify();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const [captando, setCaptando] = useState<string | null>(null);
  const [progresso, setProgresso] = useState("");
  const [descartando, setDescartando] = useState<Resultado | null>(null);
  const [itensDe, setItensDe] = useState<Resultado | null>(null);
  const { data: oportunidades, isPending, error } = useQuery({
    queryKey: ["novas_oportunidades"],
    queryFn: async (): Promise<Resultado[]> => {
      const { data, error: erro } = await supabase.rpc("buscar_pncp", { so_favoritas: true });
      if (erro) throw new Error(erro.message);
      return ((data ?? []) as Resultado[]).filter((r) => !r.licitacao_id);
    },
  });

  async function captar(r: Resultado) {
    const controle = lerControlePncp(r.numero_controle_pncp);
    if (!controle) return;
    setCaptando(r.numero_controle_pncp);
    try {
      const { itens, existente, preparacao } = await cadastrarDoPncp(controle, setProgresso);
      notify(
        existente
          ? "Esta licitação já estava captada."
          : `Licitação captada com ${itens} item(ns).${preparacao ? ` ${textoDaPreparacao(preparacao)}.` : ""}`,
        { type: existente ? "info" : "success", autoHideDuration: 12000 },
      );
      refresh();
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    } finally {
      setCaptando(null);
    }
  }

  if (isPending) return <CircularProgress size={24} />;
  if (error) return <Alert severity="error">{error.message}</Alert>;
  if (!oportunidades?.length) {
    return (
      <Alert
        severity="info"
        action={
          <Button color="inherit" size="small" onClick={() => navigate("/pncp_contratacao")}>
            Buscar licitações
          </Button>
        }
      >
        Nenhuma nova oportunidade. Na busca, marque a estrela das licitações que quer analisar.
      </Alert>
    );
  }

  return (
    <Stack spacing={1}>
      {oportunidades.map((r) => {
        const link = linkPncp(r.numero_controle_pncp);
        return (
          <Card key={r.numero_controle_pncp} variant="outlined">
            <CardContent sx={{ pb: "12px !important" }}>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 0.5 }}>
                <ChipPrazo fim={r.data_encerramento_proposta} />
                <Typography variant="caption" color="text.secondary">
                  {r.modalidade_nome} {r.numero}
                  {r.srp ? " · Registro de preços" : ""} · {r.municipio ? `${r.municipio}/` : ""}
                  {r.uf} · {nomeEsfera(r.esfera)}
                  {r.sistema ? ` · ${r.sistema}` : ""}
                </Typography>
              </Stack>
              <Typography variant="subtitle2">{r.orgao_nome}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {r.objeto}
              </Typography>
              {r.anotacao && (
                <Typography variant="body2" sx={{ mt: 0.5, px: 1, py: 0.5, bgcolor: "action.hover", borderRadius: 1 }}>
                  {r.anotacao}
                </Typography>
              )}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "center" }} sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Valor estimado {r.valor_total_estimado ? formatarMoeda(r.valor_total_estimado) : "sigiloso ou não informado"} · propostas até{" "}
                  {formatarDataHora(r.data_encerramento_proposta)}
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  <Button size="small" onClick={() => setItensDe(r)}>
                    Ver itens
                  </Button>
                  {link && (
                    <Button size="small" component={Link} href={link} target="_blank" rel="noopener noreferrer">
                      PNCP
                    </Button>
                  )}
                  <Button size="small" color="error" onClick={() => setDescartando(r)}>
                    Descartar
                  </Button>
                  <Button size="small" variant="contained" disabled={captando !== null} onClick={() => void captar(r)}>
                    {captando === r.numero_controle_pncp ? progresso || "Captando…" : "Captar"}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        );
      })}
      <DialogoItens resultado={itensDe} termos={[]} aoFechar={() => setItensDe(null)} />
      {descartando && <DialogoDescartarOportunidade oportunidade={descartando} aoFechar={() => setDescartando(null)} />}
    </Stack>
  );
}

/* ------------------------------------ Lista ------------------------------------ */

const filtros = [
  <TextInput key="busca" source="busca@ilike" label="Pesquisar órgão, número, processo ou objeto" parse={normalizarBusca} alwaysOn resettable sx={{ minWidth: 320 }} />,
  <ReferenceInput key="pessoa" source="pessoa_id" reference="pessoa" sort={{ field: "nome", order: "ASC" }}>
    <AutocompleteInput optionText="nome" label="Órgão" filterToQuery={(t: string) => (t ? { "busca@ilike": normalizarBusca(t) } : {})} />
  </ReferenceInput>,
  <ReferenceInput key="portal" source="portal_id" reference="portal" sort={{ field: "nome", order: "ASC" }}>
    <SelectInput optionText="nome" label="Portal" />
  </ReferenceInput>,
  <ReferenceInput key="responsavel" source="responsavel_id" reference="usuario" sort={{ field: "nome", order: "ASC" }}>
    <SelectInput optionText="nome" label="Responsável" />
  </ReferenceInput>,
  <DateInput key="de" source="data_hora_sessao@gte" label="Sessão a partir de" />,
  <DateInput key="ate" source="data_hora_sessao@lte" label="Sessão até" />,
];

function ListaDoGrupo({ grupo }: { grupo: string }) {
  const celular = useCelular();
  return (
    <List
      key={grupo}
      resource="v_licitacao"
      title="Licitações"
      filter={filtroDoGrupo(grupo)}
      sort={{ field: "data_hora_sessao", order: "DESC" }}
      filters={filtros}
      actions={
        <TopToolbar>
          <CreateButton resource="licitacao" label="Nova licitação" />
        </TopToolbar>
      }
      exporter={false}
      empty={false}
      sx={{ "& .RaList-main": { mt: 0 } }}
    >
      {celular ? (
        <SimpleList
          primaryText={(r) => `${r.orgao_nome} — ${r.numero}`}
          secondaryText={(r) => `${formatarDataHora(r.data_hora_sessao)} · ${r.objeto}`}
          tertiaryText={(r) => <SeletorStatus licitacaoId={r.id} status={r.status} />}
          linkType={(r) => `/licitacao/${r.id}`}
        />
      ) : (
        <Datagrid bulkActionButtons={false} rowClick={(id) => `/licitacao/${id}`}>
          <TextField source="orgao_nome" />
          <TextField source="numero" />
          <TextField source="modalidade_nome" emptyText="—" />
          <FunctionField
            source="objeto"
            render={(r) => (
              <Typography variant="body2" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", maxWidth: 420 }}>
                {r.objeto}
              </Typography>
            )}
          />
          <FunctionField source="data_hora_sessao" render={(r) => formatarDataHora(r.data_hora_sessao)} />
          <FunctionField
            source="status"
            render={(r) => (
              <Stack spacing={0.5} alignItems="flex-start">
                <SeletorStatus licitacaoId={r.id} status={r.status} />
                {r.status === "proposta_pronta" && r.data_hora_sessao && new Date(r.data_hora_sessao).getTime() <= Date.now() ? (
                  <Typography variant="caption" color="warning.main" fontWeight={600}>
                    ⚠ Sessão iniciada: a proposta foi enviada?
                  </Typography>
                ) : ["resultado", "recurso", "homologada"].includes(r.status) && (r.resultado_geral === null || r.resultado_geral === "pendente") ? (
                  <Typography variant="caption" color="warning.main" fontWeight={600}>
                    ⚠ {r.resultado_geral === null ? "Registrar o resultado" : "Resultado incompleto"}
                  </Typography>
                ) : (
                  r.resultado_geral && (
                    <Typography variant="caption" color={String(r.resultado_geral).startsWith("ganha") ? "success.main" : "text.secondary"}>
                      {NOME_RESULTADO[r.resultado_geral as string] ?? ""}
                    </Typography>
                  )
                )}
              </Stack>
            )}
          />
          <FunctionField source="valor_estimado_total" textAlign="right" render={(r) => formatarMoeda(r.valor_estimado_total)} />
          <FunctionField source="valor_proposto_total" textAlign="right" render={(r) => formatarMoeda(r.valor_proposto_total)} />
        </Datagrid>
      )}
    </List>
  );
}

/** Colunas do quadro (D109): as etapas do grupo escolhido no painel. */
function colunasDoGrupo(grupo: string): string[] {
  if (grupo === "andamento") return EM_ANDAMENTO;
  if (grupo === "todas") return [...EM_ANDAMENTO, "homologada", "descartada"];
  return [grupo];
}

export function LicitacaoLista() {
  const celular = useCelular();
  const [grupo, setGrupo] = useStore<string>("licitacoes.grupo", "andamento");
  const [visao, setVisao] = useStore<"quadro" | "lista">("licitacoes.visao", "quadro");
  return (
    <Box sx={{ mt: 2, minWidth: 0 }}>
      <PainelGrupos grupo={grupo} aoEscolher={setGrupo} />
      {grupo === "oportunidade" ? (
        <>
          <Title title="Licitações — novas oportunidades" />
          <NovasOportunidades />
        </>
      ) : (
        <>
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <ToggleButtonGroup size="small" exclusive value={visao} onChange={(_, v: "quadro" | "lista" | null) => v && setVisao(v)}>
              <ToggleButton value="quadro">
                <QuadroIcon fontSize="small" sx={{ mr: 0.5 }} /> Quadro
              </ToggleButton>
              <ToggleButton value="lista">
                <ListaIcon fontSize="small" sx={{ mr: 0.5 }} /> Lista
              </ToggleButton>
            </ToggleButtonGroup>
            {visao === "quadro" && <CreateButton resource="licitacao" label="Nova licitação" />}
          </Stack>
          {visao === "quadro" ? (
            <>
              <Title title="Licitações" />
              <QuadroLicitacoes filtro={filtroDoGrupo(grupo)} colunas={colunasDoGrupo(grupo)} celular={celular} />
            </>
          ) : (
            <ListaDoGrupo grupo={grupo} />
          )}
        </>
      )}
    </Box>
  );
}
